import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, TextField, Button, Box, CircularProgress, } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./CreateSite.css";
import axios from "axios";

const CreateCustomer = () => {
  const [customerData, setCustomerData] = useState({
    customer_name: "",
    organization_email: "",
    customer_address: "",
    contact_person_name: "",
    contact_person_email: "",
    contact_person_designation: "",
    contact_no: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_DJANGO_URL;

  // const nameRegex = /^[A-Za-z]{2,20}$/;

  const emailRegex =
    /^(?!.*\.\.)(?!\.)([A-Za-z0-9._%+-]+)@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  // Starts from 6-9 and exactly 10 digits
  const phoneRegex = /^[6-9]\d{9}$/;

  // Designation: only alphabets, numbers and spaces
  const designationRegex = /^[A-Za-z0-9 ]{2,50}$/;

  // Customer name: alphabets, numbers and spaces
  const customerNameRegex = /^[A-Za-z0-9 ]{2,50}$/;

  // Address
  const addressRegex = /^[A-Za-z0-9\s,./-]{5,100}$/;

  const validateForm = (fieldName = null, fieldValue = null) => {
    const newErrors = { ...errors };

    const fieldsToCheck = fieldName
      ? { [fieldName]: fieldValue }
      : customerData;

    for (let key in fieldsToCheck) {
      const value = fieldsToCheck[key].trim();

      switch (key) {
        case "customer_name":
          if (!value) {
            newErrors.customer_name = "Customer name is required";
          } else if (value.length < 2 || value.length > 50) {
            newErrors.customer_name =
              "Customer name must be between 2 and 50 characters";
          } else if (!customerNameRegex.test(value)) {
            newErrors.customer_name =
              "Only letters, numbers and single spaces are allowed";
          } else {
            delete newErrors.customer_name;
          }
          break;

        case "organization_email":
        case "contact_person_email":
          if (!value) {
            newErrors[key] = "Email is required";
          } else if (value.length < 2 || value.length > 50) {
            newErrors[key] =
              "Email must be between 2 and 50 characters";
          } else if (value.includes("..")) {
            newErrors[key] =
              "Multiple dots are not allowed";
          } else if (
            value.startsWith(".") ||
            value.endsWith(".")
          ) {
            newErrors[key] =
              "Dot not allowed at start/end";
          } else if (!emailRegex.test(value)) {
            newErrors[key] = "Invalid email format";
          } else {
            delete newErrors[key];
          }
          break;

        case "customer_address":
          if (!value) {
            newErrors.customer_address = "Address is required";
          } else if (value.length < 5 || value.length > 100) {
            newErrors.customer_address =
              "Address must be between 5 and 100 characters";
          } else if (!addressRegex.test(value)) {
            newErrors.customer_address =
              "Invalid address";
          } else {
            delete newErrors.customer_address;
          }
          break;

        case "contact_person_name":
          if (!value) {
            newErrors.contact_person_name =
              "Contact person name is required";
          } else if (value.length < 2 || value.length > 50) {
            newErrors.contact_person_name =
              "Contact person name must be between 2 and 50 characters";
          } else if (!customerNameRegex.test(value)) {
            newErrors.contact_person_name =
              "Only letters, numbers and single spaces are allowed";
          } else {
            delete newErrors.contact_person_name;
          }
          break;
        case "contact_person_designation":
          if (!value) {
            newErrors.contact_person_designation =
              "Designation is required";
          } else if (
            value.length < 2 ||
            value.length > 50
          ) {
            newErrors.contact_person_designation =
              "Designation must be between 2 and 50 characters";
          } else if (!designationRegex.test(value)) {
            newErrors.contact_person_designation =
              "Special characters are not allowed";
          } else {
            delete newErrors.contact_person_designation;
          }
          break;

        case "contact_no":
          if (!value) {
            newErrors.contact_no =
              "Contact number is required";
          } else if (!phoneRegex.test(value)) {
            newErrors.contact_no =
              "Must start from 6-9 and be exactly 10 digits";
          } else {
            delete newErrors.contact_no;
          }
          break;

        default:
          break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    // Customer Name
    if (name === "customer_name") {
      value = value.replace(/^\s+/, "");
      value = value.replace(/\s{2,}/g, " ");
    }

    // Contact Person Name (Only alphabets)
    if (name === "contact_person_name") {
      value = value.replace(/^\s+/, "");
      value = value.replace(/\s{2,}/g, " ");
    }

    // Designation (No special characters)
    if (name === "contact_person_designation") {
      value = value.replace(/[^A-Za-z0-9 ]/g, "");
      value = value.replace(/^\s+/, "");
      value = value.replace(/\s{2,}/g, " ");
    }

    // Address
    if (name === "customer_address") {
      value = value.replace(/^\s+/, "");
      value = value.replace(/\s{2,}/g, " ");
    }

    // Emails
    if (
      name === "organization_email" ||
      name === "contact_person_email"
    ) {
      value = value.replace(/^\s+/, "").toLowerCase();
    }

    // Phone
    if (name === "contact_no") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    setCustomerData((prev) => ({
      ...prev,
      [name]: value,
    }));

    validateForm(name, value);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");

      if (!token) {
        toast.error("No token found! Please login first.");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/create_customer_admin/`,
        customerData,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Customer created:", response.data);
      toast.success("Customer created successfully!");

      setCustomerData({
        customer_name: "",
        organization_email: "",
        customer_address: "",
        contact_person_name: "",
        contact_person_email: "",
        contact_person_designation: "",
        contact_no: "",
      });
    } catch (error) {
      console.error(
        "Error creating customer:",
        error.response?.data || error.message
      );

      if (error.response?.status === 400) {
        toast.error("Customer already exists");
      } else {
        toast.error("Failed to create customer");
      }
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      className="create-site-form-container"
      onSubmit={handleFormSubmit}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            className="create-site-input"
            label="Customer Name"
            name="customer_name"
            value={customerData.customer_name}
            onChange={handleChange}
            required
            error={!!errors.customer_name}
            helperText={errors.customer_name || ""}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            className="create-site-input"
            label="Organization Email"
            name="organization_email"
            value={customerData.organization_email}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === " ") e.preventDefault();
            }}
            required
            error={!!errors.organization_email}
            helperText={errors.organization_email || ""}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            className="create-site-input"
            label="Address"
            name="customer_address"
            value={customerData.customer_address}
            onChange={handleChange}
            required
            error={!!errors.customer_address}
            helperText={errors.customer_address || ""}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            className="create-site-input"
            label="Contact Person Name"
            name="contact_person_name"
            value={customerData.contact_person_name}
            onChange={handleChange}
            required
            error={!!errors.contact_person_name}
            helperText={errors.contact_person_name || ""}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            className="create-site-input"
            label="Contact Person Email"
            name="contact_person_email"
            value={customerData.contact_person_email}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === " ") e.preventDefault();
            }}
            required
            error={!!errors.contact_person_email}
            helperText={errors.contact_person_email || ""}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            className="create-site-input"
            label="Contact Person Designation"
            name="contact_person_designation"
            value={customerData.contact_person_designation}
            onChange={handleChange}
            required
            error={!!errors.contact_person_designation}
            helperText={errors.contact_person_designation || ""}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            className="create-site-input"
            label="Contact No"
            name="contact_no"
            value={customerData.contact_no}
            onChange={handleChange}
            required
            error={!!errors.contact_no}
            helperText={errors.contact_no || ""}
          />
        </Grid>
      </Grid>

      <Box
        className="submit-btn-wrapper"
        display="flex"
        gap={2}
        mt={3}
      >
        <Button
          type="button"
          variant="outlined"
          size="large"
          className="submit-btn cancel-btn"
          onClick={() => {
            setCustomerData({
              customer_name: "",
              organization_email: "",
              customer_address: "",
              contact_person_name: "",
              contact_person_email: "",
              contact_person_designation: "",
              contact_no: "",
            });

            setErrors({});
            navigate("/dashboard");
          }}
        >
          Cancel
        </Button>

        {/* <Button
          type="submit"
          variant="contained"
          size="large"
          className="submit-btn"
        >
          Create Customer
        </Button> */}
        <Button
          type="submit"
          variant="contained"
          size="large"
          className="submit-btn"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Create Customer"
          )}
        </Button>
      </Box>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme="light"
      />
    </Box>
  );
};

export default CreateCustomer;